import {
  createBinnedDataBufferFromData,
  GeckoBinnedDataBuffer,
} from 'features/dataLayers/geckoBinnedDataBuffer'
import UnpackCDFDataWorker from 'features/dataLayers/load/unpackCdfData.worker?worker'
import { useFileSignedUrlFetcher } from 'features/rolodex'
import { isNil, memoize } from 'lodash'
import { useCallback } from 'react'
import { GeckoComponentDataFrame, GeckoComponentRect } from 'types'
import { cdfDataFileName } from 'utils/constants'

import { useBinnedDataBufferCache } from './useBinnedDataBufferCache'
import { useCdfDataGenerator } from './useCdfDataGenerator'
import { useFetchDuckDbFeatureFlag } from './useFetchDuckDbFeatureFlag'

/**
 * A hook that returns a callback function that can be used to get the data for
 * a given entry (any object with `id` and `generation` fields).
 *
 * @param allowCache    If false, the cache will be ignored and the data will only be fetched from rolodex-proper.
 *                      This will also make it so that the returned function will not re-initialize on cache change, which
 *                      is needed for when we're fetching data that will be used to generate something for the Rolodex-Cache,
 *                      as if we don't ignore the cache in memos we'll end up in an infinite loop.
 *
 * @returns     A function with two parameters: `cdf: GeckoComponentDataFrame`, and `cropSelection: GeckoComponentRect`, where `cdf` is
 *              the cdf to fetch data for and `cropSelection` is an optional x/y filter.  The function returns data for the given `cdf` with the `cropSelection` filter applied.
 *              (as a `Promise<ReadonlyArray<number>>`) or `undefined` if it couldn't be fetched
 *
 *
 */
export function useCdfDataFetcher(
  allowCache: boolean = true
): (
  entry: GeckoComponentDataFrame,
  cropSelection?: GeckoComponentRect
) => Promise<GeckoBinnedDataBuffer | undefined> {
  const dataGeneratorCallback = useCdfDataGenerator()
  const legacyDataFetcherCallback = useLegacyCdfDataFetcher(allowCache)
  const featureFlagFetcher = useFetchDuckDbFeatureFlag()
  const fetcher = useCallback(
    (cdf: GeckoComponentDataFrame, cropSelection?: GeckoComponentRect) => {
      if (!featureFlagFetcher) {
        return Promise.resolve(undefined)
      }

      return featureFlagFetcher.then((duckDbFeatureFlag) =>
        duckDbFeatureFlag.isEntryEnabled(cdf)
          ? dataGeneratorCallback(cdf, cropSelection)
          : legacyDataFetcherCallback(cdf)
      )
    },
    [featureFlagFetcher, dataGeneratorCallback, legacyDataFetcherCallback]
  )
  return fetcher
}

function useLegacyCdfDataFetcher(allowCache: boolean = true) {
  const {
    getCachedBinnedDataBufferAccessor,
    deleteCachedBinnedDataBuffer,
    putBinnedDataBufferInCache,
  } = useBinnedDataBufferCache()
  const { fetchSignedUrl, fetchSignedUrlFromRolodexOnly } =
    useFileSignedUrlFetcher()

  // Get the correct fetcher here, so the useCallback will only re-initialize when needed.
  const fetcherToUse = allowCache
    ? fetchSignedUrl
    : fetchSignedUrlFromRolodexOnly

  /**
   * A function that fetches the data for a given CDF, caching the result
   *
   * @param cdf       The cdf to fetch data for
   * @param path      The rolodex-file-path to the data file to fetch (defaults to `cdfDataFileName`)
   *
   * @returns         The data for the CDF, or undefined if it couldn't be fetched
   */
  return useCallback(
    async (
      cdf: GeckoComponentDataFrame,
      path: string = cdfDataFileName
    ): Promise<GeckoBinnedDataBuffer | undefined> => {
      const existingBinnedDataBuffer = getCachedBinnedDataBufferAccessor(
        cdf.id,
        path
      )
      if (existingBinnedDataBuffer?.generation === cdf.generation) {
        return await existingBinnedDataBuffer.retrieveBinnedDataBuffer()
      } else if (
        !isNil(existingBinnedDataBuffer) &&
        existingBinnedDataBuffer?.generation !== cdf.generation
      ) {
        deleteCachedBinnedDataBuffer(cdf.id, path)
      }

      function valueGetterFn(): Promise<GeckoBinnedDataBuffer> {
        return new Promise((resolve, reject) => {
          // get the signed URL
          fetcherToUse({
            queryOrQueries: { entry_id: cdf.id, path: path },
          }).then((signedUrl) => {
            // fetch the data from that signed URL
            if (!signedUrl?.url) {
              console.warn(
                `Failed to fetch signed URL for the path "${path}" data on an entry with id "${cdf.id}"`
              )
              reject("Couldn't fetch signed URL")
              return
            }

            fetchCdfData(signedUrl.url).then((entryData) => {
              resolve(
                createBinnedDataBufferFromData(
                  new Float32Array(entryData.array),
                  cdf
                )
              )
            })
          })
        })
      }

      const memoizedGetterFn = memoize(valueGetterFn)

      putBinnedDataBufferInCache({
        id: cdf.id,
        filePath: path,
        generation: cdf.generation,
        data: memoizedGetterFn,
      })

      return await memoizedGetterFn()
    },
    [
      deleteCachedBinnedDataBuffer,
      fetcherToUse,
      getCachedBinnedDataBufferAccessor,
      putBinnedDataBufferInCache,
    ]
  )
}

/**
 * A function that pings a (rolodex signed) url to fetch the data for a CDF
 * and unpacks it into a number array in a web worker.
 */
const fetchCdfData = (url: string) => {
  return Promise.resolve(
    new Promise<{ url: string; array: number[] }>((resolve, reject) => {
      try {
        fetch(url).then((r) => {
          if (!r.ok) {
            reject()
            return
          }

          // Use a web worker to unpack the data, as unpacking it can be
          // quite time-intensive, and we don't want to block the main thread
          const worker = new UnpackCDFDataWorker()
          worker.onmessage = async (message) => {
            worker.terminate()
            resolve({ url, array: message.data as Array<number> })
          }
          worker.onerror = async (error) => {
            console.error(error)
            worker.terminate()
            reject()
          }

          // Don't do an await here since we might be unpacking millions of
          // thickness readings from a CDF. Instead, pass the buffer over
          // to the worker, which will turn it into JSON and then pass back
          // the unpacked array.
          r.arrayBuffer().then((buffer) => {
            worker.postMessage(buffer, [buffer])
          })
        })
      } catch (error) {
        console.error(error)
      }
    })
  )
}
